import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist/build/pdf';

const pdfWorkerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url);
const MAX_PREVIEW_DIMENSION = 512;

let workerConfigured = false;

const ensurePdfWorkerConfigured = () => {
  if (workerConfigured || typeof window === 'undefined') {
    return;
  }

  GlobalWorkerOptions.workerSrc = pdfWorkerSrc.toString();
  workerConfigured = true;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const canvasToBlob = (canvas, mimeType) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((result) => {
      if (!result) {
        reject(new Error('Unable to serialize canvas content.'));
        return;
      }
      resolve(result);
    }, mimeType);
  });

const prepareImageSource = async (blob) => {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob);
      return {
        width: bitmap.width,
        height: bitmap.height,
        draw: (ctx, width, height) => ctx.drawImage(bitmap, 0, 0, width, height),
        cleanup: () => {
          if (typeof bitmap.close === 'function') {
            bitmap.close();
          }
        },
      };
    } catch (error) {
      console.warn('Creazione bitmap non riuscita, fallback su <img>', error);
    }
  }

  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
        draw: (ctx, width, height) => ctx.drawImage(image, 0, 0, width, height),
        cleanup: () => {},
      });
    };
    image.onerror = (event) => {
      URL.revokeObjectURL(objectUrl);
      reject(event instanceof Error ? event : new Error("Impossibile caricare l'immagine per il ridimensionamento."));
    };
    image.src = objectUrl;
  });
};

const resizeImageBlob = async (blob) => {
  let source;
  try {
    source = await prepareImageSource(blob);
    const reference = Math.max(source.width || 0, source.height || 0) || MAX_PREVIEW_DIMENSION;
    const scale = Math.min(1, MAX_PREVIEW_DIMENSION / reference);
    const targetWidth = Math.max(1, Math.round((source.width || MAX_PREVIEW_DIMENSION) * scale));
    const targetHeight = Math.max(1, Math.round((source.height || MAX_PREVIEW_DIMENSION) * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext('2d');
    source.draw(context, targetWidth, targetHeight);

    const resizedBlob = await canvasToBlob(canvas, 'image/png');
    const dataUrl = await blobToDataUrl(resizedBlob);

    return {
      blob: resizedBlob,
      dataUrl,
      mimeType: 'image/png',
    };
  } catch (error) {
    console.warn('Ridimensionamento immagine non riuscito, uso blob originale', error);
    const dataUrl = await blobToDataUrl(blob);
    return {
      blob,
      dataUrl,
      mimeType: blob.type || 'image/png',
    };
  } finally {
    if (source?.cleanup) {
      source.cleanup();
    }
  }
};

export const dataUrlToObjectUrl = (dataUrl) => {
  const [header, base64] = dataUrl.split(',');
  const mimeMatch = /data:(.*?);/.exec(header);
  const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';
  const binary = atob(base64);
  const length = binary.length;
  const bytes = new Uint8Array(length);
  for (let i = 0; i < length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: mimeType });
  const objectUrl = URL.createObjectURL(blob);
  return { objectUrl, mimeType };
};

export const createPreviewFromBlob = async (blob, fileName) => {
  if (typeof document === 'undefined') {
    return {
      objectUrl: null,
      dataUrl: null,
      mimeType: blob.type,
      fileName,
    };
  }

  const resized = await resizeImageBlob(blob);
  const objectUrl = URL.createObjectURL(resized.blob);
  return {
    objectUrl,
    dataUrl: resized.dataUrl,
    mimeType: resized.mimeType,
    fileName,
  };
};

export const generatePdfThumbnail = async (arrayBuffer, fileName) => {
  if (typeof document === 'undefined') {
    return {
      objectUrl: null,
      dataUrl: null,
      mimeType: 'image/png',
      fileName,
    };
  }

  ensurePdfWorkerConfigured();

  let pdf;
  let page;
  try {
    pdf = await getDocument({ data: new Uint8Array(arrayBuffer) }).promise;
    page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 1 });
    const reference = Math.max(viewport.width, viewport.height);
    const scale = Math.min(1, MAX_PREVIEW_DIMENSION / reference);
    const scaledViewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = Math.round(scaledViewport.width);
    canvas.height = Math.round(scaledViewport.height);

    await page.render({ canvasContext: context, viewport: scaledViewport }).promise;

    const blob = await canvasToBlob(canvas, 'image/png');
    const dataUrl = await blobToDataUrl(blob);
    const objectUrl = URL.createObjectURL(blob);

    return {
      objectUrl,
      dataUrl,
      mimeType: 'image/png',
      fileName,
    };
  } finally {
    if (page?.cleanup) {
      page.cleanup();
    }
    if (pdf?.destroy) {
      pdf.destroy();
    }
  }
};

export const PREVIEW_MAX_DIMENSION = MAX_PREVIEW_DIMENSION;
